<template>
  <div class="creative_chat_shell">
    <!-- Chat Header Toolbar -->
    <div class="chat_top_toolbar">
      <div class="toolbar_left">
        <div class="stream_badge">
          <span class="live_pulse"></span>
          <i class="fal fa-comments me-2"></i>
          <span class="stream_title">{{ $t("breadcrumb.chats.title") || "محادثة مباشرة" }}</span>
        </div>
        <span class="message_count_chip" v-if="messages && messages.length">
          <i class="fal fa-layer-group me-1"></i>
          {{ messages.length }} {{ $t("labels.messages") || "رسالة" }}
        </span>
      </div>

      <div class="toolbar_right">
        <div class="readonly_chip" v-if="readOnly">
          <i class="fal fa-shield-alt me-1"></i>
          <span>وضع التدقيق والمتابعة</span>
        </div>
        <button
          type="button"
          class="scroll_bottom_action"
          title="الانتقال لآخر رسالة"
          @click="scrollToBottom"
        >
          <i class="fal fa-arrow-down"></i>
        </button>
      </div>
    </div>

    <!-- Chat Messages Viewport -->
    <div class="creative_chat_viewport" ref="chatViewport">
      <!-- Empty State -->
      <div class="chat_empty_view" v-if="!messages || messages.length === 0">
        <div class="empty_icon_sphere">
          <i class="fal fa-comments"></i>
        </div>
        <h4 class="empty_title">لا توجد رسائل في هذه المحادثة حتى الآن</h4>
        <p class="empty_desc">سجل المحادثة فارغ أو لم تبدأ المحادثة بعد بين الطرفين.</p>
      </div>

      <!-- Messages Stream -->
      <div class="chat_stream_container" v-else v-chat-scroll="{ smooth: true }">
        <div
          v-for="(message, index) in messages"
          :key="message.id || index"
          class="message_stream_item"
        >
          <!-- Date Separator -->
          <div class="stream_date_divider" v-if="shouldShowDate(index)">
            <span class="divider_badge">
              <i class="fal fa-calendar-day me-1"></i>
              {{ formatDate(message.created_at) }}
            </span>
          </div>

          <!-- Message Row -->
          <div
            class="messageOwner"
            :class="{
              sender: isSender(message),
              receiver: !isSender(message),
            }"
          >
            <!-- User Avatar -->
            <div class="avatar_column">
              <img
                v-if="message.sender_data && message.sender_data.image"
                class="user_img"
                :src="message.sender_data.image"
                :alt="getSenderName(message)"
                @error="onAvatarError($event)"
              />
              <div
                v-else
                class="user_img avatar_initials"
                :class="isSender(message) ? 'sender_bg' : 'receiver_bg'"
              >
                {{ getSenderInitials(message) }}
              </div>
            </div>

            <!-- Message Card Content -->
            <div class="content">
              <!-- Sender Header Info Inside Bubble -->
              <div class="bubble_party_header">
                <span class="party_name">{{ getSenderName(message) }}</span>
                <span
                  class="party_role_badge"
                  :class="isSender(message) ? 'role_sender' : 'role_receiver'"
                >
                  <i :class="isSender(message) ? 'fal fa-user me-1' : 'fal fa-store me-1'"></i>
                  {{ isSender(message) ? 'العميل' : 'مزود الخدمة' }}
                </span>
              </div>

              <!-- Media Attachment: Image -->
              <div
                class="media"
                v-if="
                  message.message_type == 'image' ||
                  message.message_type == 'video' ||
                  message.message_type == 'audio'
                "
              >
                <div
                  v-if="message.message_type == 'image'"
                  class="image_media_box"
                  @click="openImage(message.message)"
                >
                  <div class="image_preview_wrapper">
                    <img :src="message.message" alt="Media Attachment" />
                    <div class="zoom_overlay">
                      <i class="fal fa-search-plus"></i>
                    </div>
                  </div>
                  <p v-if="message.descreption" class="descreption">
                    {{ message.descreption }}
                  </p>
                </div>

                <div v-if="message.message_type == 'video'" class="video_media_box">
                  <video controls preload="metadata">
                    <source :src="message.message" type="video/mp4" />
                  </video>
                  <p v-if="message.descreption" class="descreption">
                    {{ message.descreption }}
                  </p>
                </div>

                <div v-if="message.audio_src || message.message_type == 'audio'" class="audio_media">
                  <div class="audio_player_container">
                    <div class="audio_icon_box">
                      <i class="fal fa-microphone"></i>
                    </div>
                    <audio controls>
                      <source :src="message.audio_src || message.message" type="audio/ogg" />
                      <source :src="message.audio_src || message.message" type="audio/mpeg" />
                    </audio>
                  </div>
                </div>
              </div>

              <!-- Text Message -->
              <p
                v-html="formatMessageText(message.message)"
                v-if="
                  message.message_type == 'text' ||
                  message.message_type == 'offer' ||
                  (!message.message_type && message.message)
                "
                class="text"
              ></p>

              <!-- Meta Footer with Time & Checkmark -->
              <div class="bubble_footer">
                <span class="created_at">
                  {{ formatTime(message.created_at) }}
                </span>
                <span class="check_icon">
                  <i class="fal fa-check-double"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Read-Only Audit Notice Footer -->
    <div class="chat_audit_footer" v-if="readOnly">
      <div class="audit_notice_wrapper">
        <i class="fal fa-shield-check"></i>
        <span>سجل المراقبة الإدارية: يتم حفظ جميع الرسائل والوسائط في هذه المحادثة للأرشفة والمتابعة فقط.</span>
      </div>
    </div>

    <!-- Lightbox Image Modal -->
    <v-dialog v-model="imageModalOpen" max-width="750" content-class="chat_lightbox_modal">
      <div class="lightbox_body" v-if="imageModalOpen">
        <button type="button" class="lightbox_close_btn" @click="imageModalOpen = false">
          <i class="fal fa-times"></i>
        </button>
        <img :src="selectedImageUrl" alt="Attachment Preview" />
      </div>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "ChatComp",
  props: {
    chatObj: {
      type: [Object, Array],
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      messages: [],
      imageModalOpen: false,
      selectedImageUrl: "",
    };
  },

  watch: {
    chatObj: {
      immediate: true,
      handler(val) {
        this.messages = Array.isArray(val) ? [...val] : [];
      },
    },
  },

  methods: {
    isSender(message) {
      if (!message || !message.sender_data) return true;
      if (
        this.messages &&
        this.messages.length > 0 &&
        this.messages[0] &&
        this.messages[0].sender_data
      ) {
        return message.sender_data.id == this.messages[0].sender_data.id;
      }
      return true;
    },

    getSenderName(message) {
      if (message && message.sender_data && message.sender_data.first_name) {
        return message.sender_data.first_name;
      }
      return this.isSender(message) ? "العميل" : "مزود الخدمة";
    },

    getSenderInitials(message) {
      const name = this.getSenderName(message);
      return name ? name.trim().charAt(0).toUpperCase() : "U";
    },

    onAvatarError(event) {
      event.target.style.display = "none";
      if (event.target.nextElementSibling) {
        event.target.nextElementSibling.style.display = "flex";
      }
    },

    formatTime(dateStr) {
      if (!dateStr) return "";
      // Handle "PM 06:45 2026-09-30" format
      const ampmMatch = dateStr.match(/^(AM|PM)\s*(\d{1,2}:\d{2})/i);
      if (ampmMatch) {
        return `${ampmMatch[1]} ${ampmMatch[2]}`;
      }
      // Handle "2026-09-30 18:47:14"
      const timeMatch = dateStr.match(/(\d{1,2}:\d{2})(:\d{2})?/);
      if (timeMatch) {
        return timeMatch[1];
      }
      return dateStr;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      const match = dateStr.match(/\d{4}-\d{2}-\d{2}/);
      if (match) return match[0];
      return dateStr;
    },

    shouldShowDate(index) {
      if (index === 0) return true;
      const current = this.formatDate(this.messages[index]?.created_at);
      const prev = this.formatDate(this.messages[index - 1]?.created_at);
      return current && current !== prev;
    },

    formatMessageText(txt) {
      if (!txt) return "";
      return String(txt).replace(/(\r\n|\n|\r)/gm, "<br />");
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const viewport = this.$refs.chatViewport;
        if (viewport) {
          viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
        }
      });
    },

    openImage(url) {
      if (!url) return;
      this.selectedImageUrl = url;
      this.imageModalOpen = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.creative_chat_shell {
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 12px 35px -8px rgba(0, 0, 0, 0.07), 0 0 0 1px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(226, 232, 240, 0.9);
  margin-bottom: 30px;
  position: relative;
}

/* ==================== Top Toolbar ==================== */
.chat_top_toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #fbfbfc 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  flex-wrap: wrap;
  gap: 12px;

  .toolbar_left {
    display: flex;
    align-items: center;
    gap: 12px;

    .stream_badge {
      display: inline-flex;
      align-items: center;
      font-size: 15px;
      font-weight: 700;
      color: #1e293b;

      .live_pulse {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #10b981;
        margin-inline-end: 8px;
        box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
        animation: pulseRadar 2s infinite ease-in-out;
      }

      i {
        color: var(--main);
        font-size: 18px;
      }
    }

    .message_count_chip {
      background: rgba(161, 122, 186, 0.12);
      color: var(--main);
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
    }
  }

  .toolbar_right {
    display: flex;
    align-items: center;
    gap: 12px;

    .readonly_chip {
      display: inline-flex;
      align-items: center;
      background: #f1f5f9;
      color: #64748b;
      padding: 5px 14px;
      border-radius: 20px;
      font-size: 13px;
      font-weight: 600;
      border: 1px solid #e2e8f0;

      i {
        color: #94a3b8;
      }
    }

    .scroll_bottom_action {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      color: #64748b;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
      transition: all 0.2s ease;

      &:hover {
        background: var(--main);
        color: #ffffff;
        border-color: var(--main);
        transform: translateY(-2px);
      }
    }
  }
}

/* ==================== Viewport & Canvas ==================== */
.creative_chat_viewport {
  position: relative;
  min-height: 480px;
  max-height: 620px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 20px;
  background-color: #f8fafc;
  background-image: 
    radial-gradient(rgba(161, 122, 186, 0.06) 1.5px, transparent 1.5px),
    radial-gradient(rgba(0, 0, 0, 0.02) 1.5px, transparent 1.5px);
  background-size: 32px 32px;
  background-position: 0 0, 16px 16px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(161, 122, 186, 0.35);
    border-radius: 10px;
    &:hover {
      background: var(--main);
    }
  }
}

/* ==================== Date Separator ==================== */
.stream_date_divider {
  display: flex;
  justify-content: center;
  margin: 18px 0;
  position: relative;

  .divider_badge {
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    padding: 5px 18px;
    border-radius: 30px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(226, 232, 240, 0.8);
    display: inline-flex;
    align-items: center;
  }
}

/* ==================== Empty View ==================== */
.chat_empty_view {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 380px;
  text-align: center;
  padding: 40px 20px;

  .empty_icon_sphere {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(161, 122, 186, 0.12) 0%, rgba(161, 122, 186, 0.05) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 34px;
    color: var(--main);
    margin-bottom: 18px;
    box-shadow: 0 8px 20px rgba(161, 122, 186, 0.1);
  }

  .empty_title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }

  .empty_desc {
    font-size: 14px;
    color: #64748b;
    max-width: 360px;
    margin: 0 auto;
  }
}

/* ==================== Message Owner Bubbles ==================== */
.messageOwner {
  display: flex;
  align-items: flex-end;
  margin-bottom: 20px;
  width: 100%;
  gap: 12px;
  transition: transform 0.15s ease;

  .avatar_column {
    flex-shrink: 0;

    .user_img {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      object-fit: cover;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
      border: 2px solid #ffffff;
      display: block;
    }

    .avatar_initials {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: 700;
      color: #ffffff;
      user-select: none;

      &.sender_bg {
        background: linear-gradient(135deg, #a17aba 0%, #7c4d99 100%);
      }

      &.receiver_bg {
        background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
      }
    }
  }

  .content {
    max-width: min(72%, 620px);
    border-radius: 20px;
    padding: 14px 18px;
    position: relative;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.04);
    word-wrap: break-word;
    word-break: break-word;

    .bubble_party_header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 6px;
      font-size: 13px;

      .party_name {
        font-weight: 700;
      }

      .party_role_badge {
        font-size: 11px;
        font-weight: 600;
        padding: 2px 8px;
        border-radius: 12px;
        display: inline-flex;
        align-items: center;
      }
    }

    p.text {
      font-size: 14.5px;
      line-height: 1.6;
      margin: 0 0 6px 0;
      font-weight: 400;
    }

    .bubble_footer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 6px;
      font-size: 11.5px;

      .created_at {
        opacity: 0.85;
      }

      .check_icon {
        font-size: 13px;
        opacity: 0.9;
      }
    }
  }

  /* Sender (Customer / Brand Purple side) */
  &.sender {
    justify-content: flex-start;

    .avatar_column {
      order: 1;
    }

    .content {
      order: 2;
      background: linear-gradient(135deg, #9d71b8 0%, #7e4d9c 100%);
      color: #ffffff;
      border-radius: 20px 20px 20px 4px;
      box-shadow: 0 6px 20px rgba(126, 77, 156, 0.25);

      .bubble_party_header {
        .party_name {
          color: #ffffff;
        }
        .party_role_badge.role_sender {
          background: rgba(255, 255, 255, 0.2);
          color: #ffffff;
        }
      }

      p.text {
        color: #ffffff;
      }

      .created_at {
        color: rgba(255, 255, 255, 0.8);
      }

      .check_icon {
        color: rgba(255, 255, 255, 0.85);
      }
    }
  }

  /* Receiver (Provider / Crisp White card side) */
  &.receiver {
    justify-content: flex-end;

    .avatar_column {
      order: 2;
    }

    .content {
      order: 1;
      background: #ffffff;
      color: #1e293b;
      border: 1px solid rgba(226, 232, 240, 0.9);
      border-radius: 20px 20px 4px 20px;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.05);

      .bubble_party_header {
        .party_name {
          color: #0f172a;
        }
        .party_role_badge.role_receiver {
          background: rgba(14, 165, 233, 0.12);
          color: #0284c7;
        }
      }

      p.text {
        color: #1e293b;
      }

      .created_at {
        color: #64748b;
      }

      .check_icon {
        color: #0ea5e9;
      }
    }
  }
}

/* ==================== Media Attachments ==================== */
.media {
  margin-bottom: 8px;
  border-radius: 14px;
  overflow: hidden;

  .image_media_box {
    cursor: pointer;
    border-radius: 14px;
    overflow: hidden;

    .image_preview_wrapper {
      position: relative;
      max-width: 320px;
      max-height: 240px;
      border-radius: 12px;
      overflow: hidden;
      background: #000;

      img {
        width: 100%;
        height: 100%;
        max-height: 240px;
        object-fit: cover;
        display: block;
        transition: transform 0.3s ease;
      }

      .zoom_overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.2s ease;
        color: #ffffff;
        font-size: 24px;
      }

      &:hover {
        img {
          transform: scale(1.03);
        }
        .zoom_overlay {
          opacity: 1;
        }
      }
    }

    .descreption {
      margin-top: 6px;
      font-size: 13px;
      opacity: 0.9;
    }
  }

  .video_media_box {
    max-width: 340px;
    border-radius: 14px;
    overflow: hidden;

    video {
      width: 100%;
      border-radius: 12px;
      display: block;
      background: #000;
    }

    .descreption {
      margin-top: 6px;
      font-size: 13px;
      opacity: 0.9;
    }
  }

  .audio_media {
    .audio_player_container {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 10px;
      background: rgba(0, 0, 0, 0.05);
      border-radius: 14px;

      .audio_icon_box {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: var(--main);
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        flex-shrink: 0;
      }

      audio {
        max-width: 240px;
        height: 36px;
      }
    }
  }
}

/* ==================== Audit Notice Footer ==================== */
.chat_audit_footer {
  padding: 12px 24px;
  background: #f1f5f9;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;

  .audit_notice_wrapper {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: #64748b;
    font-weight: 500;

    i {
      color: #10b981;
      font-size: 16px;
      margin-inline-end: 8px;
    }
  }
}

/* ==================== Lightbox Modal ==================== */
.lightbox_body {
  position: relative;
  background: transparent;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    max-width: 100%;
    max-height: 85vh;
    border-radius: 16px;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
    object-fit: contain;
  }

  .lightbox_close_btn {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.7);
    color: #ffffff;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: #ef4444;
    }
  }
}

@keyframes pulseRadar {
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(16, 185, 129, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}
</style>
